import {Ref, ref, ShallowRef, shallowRef, watch} from "vue";
import Dictionary from "~/ts/library/Dictionary";
import {RTCPeerConnectionWrapper} from "~/ts/library/webRtc/RTCPeerConnectionWrapper";
import ObjectHelper from "~/ts/library/ObjectHelper";
import {toggleArrayElement} from "~/ts/library/ToggleArrayElement";
import WebRtcMediaStream from "~/ts/library/webRtc/WebRtcMediaStream";
import {FACING_MODE_ENVIRONMENT, FACING_MODE_USER, FACING_MODES, FacingMode} from "~/ts/library/webRtc/VideoFacingMode";
import Delay from "~/ts/library/Delay";
import WebRtcCallLoggerInterface from "~/ts/library/webRtc/WebRtcCallLoggerInterface";
import ChatDateHelper from "~/chat/ts/service/ChatDateHelper";
import WebRtcCallOpenApiModel = CometOperatorApplication.WebRtcCallOpenApiModel;

let audio = document.createElement("audio");
audio.loop = true;
audio.disableRemotePlayback = true;


export default abstract class AbstractWebRtcCall implements WebRtcCallLoggerInterface {
    public static incomingCalls = shallowRef<AbstractWebRtcCall[]>([]);

    private static currentCall = shallowRef<AbstractWebRtcCall>();
    private callData: Ref<WebRtcCallOpenApiModel | undefined>;
    public static realizationClass: Function & { prototype: AbstractWebRtcCall };
    private rtcPeerConnections: ShallowRef<Dictionary<RTCPeerConnectionWrapper>>;
    private waitingIceCandidates: Dictionary<string[]>;
    private useScreenShare: Ref<boolean>;
    private useMicrophone: Ref<boolean>;
    private useVideo: Ref<boolean>;
    private canRotateCamera: Ref<boolean | undefined>;
    private videoFacingMode: Ref<FacingMode>;
    private useSelfieSegmentation: Ref<boolean>;
    public webRtcMediaStream?: WebRtcMediaStream;
    private logs: string[];

    protected abstract getIceServers(): RTCIceServer[];

    public abstract onNewIncomingCall(): void;

    public abstract onFinishCall(): void;

    protected abstract makeHangUp(): Promise<void>;

    protected abstract makeJoinCall(): Promise<void>;

    protected abstract makeSendOffer(pairId: number, offer: string): Promise<void>;

    protected abstract makeSendAnswer(pairId: number, answer: string): Promise<void>;

    protected abstract makeSendPong(userIdsWithActiveConnections: number[]): Promise<void>;

    protected abstract makeSendIceCandidates(pairId: number, iceCandidates: string[]): Promise<void>;

    protected abstract makeSendSuccessConnectNotify(pairId: number): Promise<void>;

    protected abstract makeSendDisconnectNotify(pairId: number): Promise<void>;

    protected abstract makeChangeUserSettings(isAudioEnabled: boolean, isWebCamEnabled: boolean, isScreenEnabled: boolean): Promise<void>;

    protected abstract makeSendLog(logs: string[]): Promise<void>;

    protected abstract onNewCurrentCall(): void;

    public abstract isCallForThisInstance(): boolean;

    public abstract getStaticServer(): string;

    protected abstract getIncomingCallAudioSrc(): string;

    public abstract isMobile(): boolean;

    public abstract getSelfieSegmentationModelRootPath(): string;

    public abstract isSelfieSegmentationAvailable(): boolean;

    protected constructor(private id: number) {
        this.logs = [];
        this.rtcPeerConnections = shallowRef({});
        this.waitingIceCandidates = {};
        this.callData = ref();
        this.useScreenShare = ref(false);
        this.useMicrophone = ref(true);
        this.useVideo = ref(true);
        this.useSelfieSegmentation = ref(this.isSelfieSegmentationEnabledByDefault());
        this.canRotateCamera = ref();
        this.videoFacingMode = ref(FACING_MODE_USER);
    }

    abstract showWebRtcNotSupportedError(): void;

    public isWebRtcSupported(): boolean {
        return !!window.RTCPeerConnection;
    }

    protected isSelfieSegmentationEnabledByDefault() {
        return false;
    }

    public getId(): number {
        return this.id;
    }

    public async joinCall(): Promise<void> {
        try {
            this.removeThisCallFromIncoming();
            AbstractWebRtcCall.setCurrentCall(this);
            await this.makeJoinCall();
        } catch (e) {
            this.addLog('{joinCall} Error', e);
            console.error(e);
            this.hangUp();
        }
    }

    public async hangUp() {
        try {
            this.destroy();
            this.removeThisCallFromIncoming();
            await this.makeHangUp();
        } catch (e) {
            this.addLog('{hangUp} Error', e);
            console.error(e);
        }
        if (AbstractWebRtcCall.getCurrentCall()?.getId() == this.getId()) {
            AbstractWebRtcCall.setCurrentCall(undefined);
        }
    }


    private async initRotateAvailable() {
        let count = 0;
        try {
            for (let mode of FACING_MODES) {
                await WebRtcMediaStream.getUserMedia(this, {
                    video: {
                        facingMode: {
                            exact: mode
                        }
                    }
                }, true);
                count++;
            }
        } catch (e) {
            this.addLog('{initRotateAvailable} Error', e);
        }
        this.canRotateCamera.value = count === FACING_MODES.length;
    }

    private async createPeerConnection(pairId: number): Promise<RTCPeerConnectionWrapper> {
        this.addLog('{createPeerConnection} start for', pairId);
        if (!this.webRtcMediaStream) {
            this.addLog('{createPeerConnection} empty this.webRrtMediaStream');
            let callback = (value1: boolean | FacingMode, value2: boolean | FacingMode) => {
                if (value1 !== value2) {
                    this.sendUserSettings();
                }
            };
            watch(this.useScreenShare, callback);
            watch(this.useMicrophone, callback);
            watch(this.useVideo, callback);
            watch(this.videoFacingMode, callback);
            watch(this.useSelfieSegmentation, callback);
            watch(this.useVideo, (newValue) => {
                if (newValue) {
                    this.initRotateAvailable();
                }
            });

            this.addLog('{createPeerConnection} create new WebRtcMediaStream');
            this.webRtcMediaStream = new WebRtcMediaStream(
                this.isMobile(),
                this.useVideo,
                this.useSelfieSegmentation,
                this.useMicrophone,
                this.useScreenShare,
                this
            );
            this.addLog('{createPeerConnection} start initializeStream()');

            await this.webRtcMediaStream.initializeStream({
                videoFacingMode: this.videoFacingMode.value
            });
            this.addLog('{createPeerConnection} done initializeStream()');



        } else if (!this.webRtcMediaStream.getMediaStream()) {
            this.addLog('{createPeerConnection} !this.webRtcMediaStream.getMediaStream()');
            await this.webRtcMediaStream.initializeStreamIfNotInitialized();
            this.addLog('{createPeerConnection} done initializeStreamIfNotInitialized()');
        }
        this.addLog('{createPeerConnection} new RTCPeerConnectionWrapper()');
        let peer = new RTCPeerConnectionWrapper({
            webRtcMediaStream: this.webRtcMediaStream,
            pairId,
            iceServers: this.getIceServers(),
            sendIceCandidatesMethod: (candidates) => {
                try {
                    this.makeSendIceCandidates(pairId, candidates);
                    return;
                } catch (e) {
                    this.addLog('{createPeerConnection} Error', e);
                    console.error(e);
                }
                this.hangUp();
            },
            onConnect: () => {
                this.makeSendSuccessConnectNotify(pairId);
            },
            onDisconnect: () => {
                this.makeSendDisconnectNotify(pairId);
            }
        }, this);
        this.addLog('{createPeerConnection} start peer.init()');
        await peer.init();
        this.addLog('{createPeerConnection} done peer.init()');
        this.rtcPeerConnections.value = {
            ...this.rtcPeerConnections.value,
            ...{[pairId]: peer}
        };

        let waitingCandidates = this.waitingIceCandidates[pairId];
        if (waitingCandidates) {
            delete this.waitingIceCandidates[pairId];
            this.receiveIceCandidates(pairId, waitingCandidates);
        }

        this.addLog('{createPeerConnection} done');
        return peer;
    }

    public async sendOffer(pairId: number) {
        this.rtcPeerConnections.value[pairId]?.destroy();


        try {
            let peer = await this.createPeerConnection(pairId);
            let offer = await peer.generateOffer();
            await this.makeSendOffer(pairId, offer);
            return;
        } catch (e) {
            this.addLog('{sendOffer} Error', e);
            console.error(e);
        }
        this.hangUp();
    }

    public async sendAnswer(pairId: number, offer: string) {
        this.rtcPeerConnections.value[pairId]?.destroy();


        try {
            this.addLog(`{sendAnswer} Start createPeerConnection`);
            let peer = await this.createPeerConnection(pairId);
            this.addLog(`{sendAnswer} Peer created`, peer.pairId);
            let answer = await peer.generateAnswer(offer);
            this.addLog(`{sendAnswer} Answer generated`, answer);
            await this.makeSendAnswer(pairId, answer);
            this.addLog(`{sendAnswer} success`, answer);
            return;
        } catch (e) {
            this.addLog(`{sendAnswer} Error`, e);
            console.error(e);
        }

        this.hangUp();
    }

    public async receiveAnswer(pairId: number, answer: string) {
        let peer = this.rtcPeerConnections.value[pairId];
        if (peer) {
            try {
                await peer.receiveAnswer(answer);
                return;
            } catch (e) {
                this.addLog('{receiveAnswer} Error', e);
                console.error(e);
            }
        }
        this.hangUp();
    }

    public async sendPong() {
        try {
            let callData = this.getCallData();
            if (callData) {
                let userIdsWithActiveConnections = callData.users
                    .filter(user => {
                        if (user.isYou) {
                            return false;
                        }
                        let pairId = user.pair?.id;
                        if (pairId) {
                            let connection = this.getRtcPeerConnectionByPairId(pairId);
                            return connection?.isConnected() === true;
                        }
                        return false;
                    })
                    .map(user => user.id!);
                await this.makeSendPong(userIdsWithActiveConnections);
            }
        } catch (e) {

        }
    }

    public receiveIceCandidates(pairId: number, candidates: string[]) {
        this.addLog('{receiveIceCandidates}', pairId, candidates);
        let peer = this.rtcPeerConnections.value[pairId];
        if (peer) {
            try {
                peer.receiveIceCandidates(candidates);
                return;
            } catch (e) {
                this.addLog('{receiveIceCandidates} Error', e);
                console.error(e);
            }
        } else {
            if (!this.waitingIceCandidates[pairId]) {
                this.waitingIceCandidates[pairId] = [];
            }
            this.waitingIceCandidates[pairId].push(...candidates);
            return;
        }
        this.hangUp();
    }

    public getYou() {
        return this.getCallData()?.users.find(user => user.isYou);
    }

    public getCallData(): WebRtcCallOpenApiModel | undefined {
        return this.callData.value;
    }

    public setCallData(data: WebRtcCallOpenApiModel) {
        let wasEmpty = !this.callData.value;
        this.callData.value = data;
        for (let connection of this.getRtcPeerConnections()) {
            let pairId = connection.pairId;
            if (!data.users.find(user => user.pair?.id == pairId)) {
                this.removeRtcPeerConnection(connection);
            }
        }
        if (wasEmpty) {
            let you = this.getYou();
            if (you) {
                this.useMicrophone.value = you.isAudioEnabled;
                this.useVideo.value = you.isWebCamEnabled;
                this.useScreenShare.value = you.isScreenEnabled;
            }
        }
    }

    public static create(id: number): AbstractWebRtcCall | null {
        let builder = this.realizationClass as any;
        return new builder(id);
    }

    public static setCurrentCall(call?: AbstractWebRtcCall) {
        if (this.currentCall.value !== call) {
            this.currentCall.value?.destroy();
        }
        this.currentCall.value = call;
        if (call) {
            call.onNewCurrentCall();
        }
    }

    public static getCurrentCall() {
        return this.currentCall.value;
    }


    public getRtcPeerConnections(): RTCPeerConnectionWrapper[] {
        return ObjectHelper.arrayFromDictionary(this.rtcPeerConnections.value);
    }

    public getRtcPeerConnectionByPairId(pairId: number): RTCPeerConnectionWrapper | undefined {
        return this.rtcPeerConnections.value[pairId];
    }

    public isLocalVideoAvailable(): boolean {
        for (let peer of this.getRtcPeerConnections()) {
            if (peer.isLocalVideoAvailable()) {
                return true;
            }
        }
        return false;
    }

    private sendUserSettings() {
        try {
            this.makeChangeUserSettings(
                this.isUseMicrophone(),
                this.isUseVideo(),
                this.isShowScreen()
            );
        } catch (e) {
            this.addLog('{sendUserSettings} Error', e);
            console.error(e);
        }
        this.webRtcMediaStream?.initializeStream({
            videoFacingMode: this.videoFacingMode.value
        });
    }


    public isShowScreen(): boolean {
        return this.useScreenShare.value;
    }

    public toggleShowScreen(value: boolean) {
        this.useScreenShare.value = value;
    }


    public isUseMicrophone(): boolean {
        return this.useMicrophone.value;
    }

    public toggleUseMicrophone(value: boolean) {
        this.useMicrophone.value = value;
    }

    public isUseVideo(): boolean {
        return this.useVideo.value;
    }

    public isUseSelfieSegmentation(): boolean {
        return this.useSelfieSegmentation.value;
    }


    public toggleUseVideo(value: boolean) {
        this.useVideo.value = value;
    }

    public toggleFacingMode() {
        if (this.canRotateCamera.value) {
            let mode = this.videoFacingMode.value;
            this.videoFacingMode.value = mode == FACING_MODE_USER ? FACING_MODE_ENVIRONMENT : FACING_MODE_USER;
        }
    }

    public toggleSelfieSegmentation() {
        if (this.isSelfieSegmentationAvailable()) {
            this.useSelfieSegmentation.value = !this.useSelfieSegmentation.value;
        }
    }

    public isCanRotateCamera() {
        return this.canRotateCamera.value;
    }

    public destroy() {
        this.webRtcMediaStream?.destroy();
        for (let peer of this.getRtcPeerConnections()) {
            this.removeRtcPeerConnection(peer);
        }
        this.rtcPeerConnections.value = {};
    }

    private removeRtcPeerConnection(peerConnection: RTCPeerConnectionWrapper) {
        peerConnection.destroy();
        delete this.rtcPeerConnections.value[peerConnection.pairId];
    }

    public getCallTimeString(): string | undefined {
        let you = this.getCallData()?.users.find(user => user.isYou);
        if (you) {
            let start = you.dateTimeConnectionEstablished;
            if (start) {
                let result = ChatDateHelper.nowEverySecondInSeconds - start;
                if (result > 0) {
                    let minutes = parseInt((result / 60).toString());
                    let seconds = result - minutes * 60;
                    return `${ChatDateHelper.addZero(minutes)}:${ChatDateHelper.addZero(seconds)}`;
                }
            }
        }
        return undefined;
    }

    public static setIncomingCalls(list: AbstractWebRtcCall[]) {
        AbstractWebRtcCall.incomingCalls.value = [
            ...list
        ];

        try {
            if (list.length) {
                if (!audio.src) {
                    audio.src = list[0]!.getIncomingCallAudioSrc();
                }
                audio.play();
            } else {
                audio.pause();
                audio.currentTime = 0;
                audio.load();
            }
        } catch (e) {

        }
    }

    public removeThisCallFromIncoming() {
        let list = AbstractWebRtcCall.incomingCalls.value;
        toggleArrayElement(list, this, false);
        AbstractWebRtcCall.setIncomingCalls(list);
    }

    public async addLog(...logs: any[]): Promise<void> {
        let dt = (new ChatDateHelper(new Date())).toMysqlFormat();
        let log: string[] = [];
        for (let item of logs) {
            if (typeof item != "string") {
                item = JSON.stringify(item);
            }
            log.push(item);
        }

        this.logs.push(`[${dt}] ${log.join(";\t")}`);
        try {
            await Delay.make(100, `sendWebRtcLog_${this.getId()}`);
            let logs = this.logs.splice(0, this.logs.length);
            if (logs.length) {
                this.makeSendLog(logs);
            }
        } catch (e) {

        }
    }
}
